import classNames from 'classnames';
import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import ClearIcon from '@mui/icons-material/Clear';
import CustomSelectionIcon from '@mui/icons-material/GroupAdd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from '@mui/styles';

import {
  Enum,
  Language,
  ProgramTaskStatus,
} from '@vestahealthcare/common/enums';
import { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  Employee,
  PaginationType,
  PodConfig,
  PodRule,
  ProgramTask,
} from '@vestahealthcare/common/models';
import { DATE_FORMAT } from '@vestahealthcare/common/utils/constants';

import {
  Button,
  Colors,
  FilterItem,
  FilterSelectable as FilterSelectablev2,
  Fonts,
  Icon,
  IconButton,
  Panel,
  Select,
  Tooltip,
} from 'styleguide-v2';
import { SortOption } from 'styleguide-v2/src/components/Panel';

import { showGlobalError } from 'dash/src/components/GlobalMessage';
import { CacheServices } from 'dash/src/services';
import {
  GetProgramTaskParam,
  PodTaskBulkCommonParams,
  bulkOverridePodTasks,
  bulkReassignPodTasks,
  fetchPogramTasks,
} from 'dash/src/services/PodServices';
import { getServerFilters, loadNewFilters } from 'dash/src/utils/filterUtils';
import { useQueryParams } from 'dash/src/utils/useQueryParams';

import { FUTURE_MONTHS, PAST_MONTHS } from '../constants';
import { ForecastDetailTaskFilterBar } from './ForecastDetailTaskFilterBar';
import { ForecastDetailTaskFilterModal } from './ForecastDetailTaskFilterModal';
import { ForecastDetailTaskTable } from './ForecastDetailTaskTable';
import { PodTaskOverrideModal } from './PodTaskOverrideModal';
import { PodTaskReassignModal } from './PodTaskReassignModal';
import { PodTaskReviewModal } from './PodTaskReviewModal';

const uniq = (array: any[]) => [...new Set(array)];

const UNASSIGNED_TYPE = {
  value: '',
  label: translate('global.unassigned'),
} as Selectable;

const DEFAULT_PAGE_SIZE = 25;

const SORT_OPTIONS = [
  {
    label: translate('pods.metrics.forecastDetail.sort.newestType', {
      type: translate('pods.metrics.forecastDetail.table.lastCCMTouchpoint'),
    }),
    value: 'type-ccm-newest',
    sort: 'lastCCMDate desc',
  },
  {
    label: translate('pods.metrics.forecastDetail.sort.oldestType', {
      type: translate('pods.metrics.forecastDetail.table.lastCCMTouchpoint'),
    }),
    value: 'type-ccm-oldest',
    sort: 'lastCCMDate asc',
  },
  {
    label: translate('pods.metrics.forecastDetail.sort.newestType', {
      type: translate('pods.metrics.forecastDetail.table.lastAppt'),
    }),
    value: 'type-last-newest',
    sort: 'lastAppointmentDate desc',
  },
  {
    label: translate('pods.metrics.forecastDetail.sort.oldestType', {
      type: translate('pods.metrics.forecastDetail.table.lastAppt'),
    }),
    value: 'type-last-oldest',
    sort: 'lastAppointmentDate asc',
  },
  {
    label: translate('pods.metrics.forecastDetail.sort.newestType', {
      type: translate('pods.metrics.forecastDetail.table.nextAppt'),
    }),
    value: 'type-next-newest',
    sort: 'nextAppointmentDate desc',
  },
  {
    label: translate('pods.metrics.forecastDetail.sort.oldestType', {
      type: translate('pods.metrics.forecastDetail.table.nextAppt'),
    }),
    value: 'type-next-oldest',
    sort: 'nextAppointmentDate asc',
  },
];

const useStyles = makeStyles({
  actionItem: {
    fontSize: Fonts.fontSize,
    marginLeft: 0,
    width: '100%',
  },
  back: {
    fontFamily: Fonts.fontFamily,
    fontSize: `calc(${Fonts.fontSize} * 0.75)`,
  },
  date: {
    width: '15rem',
  },
  disabledOption: {
    cursor: 'default',
    opacity: 0.38,
    width: '100%',
  },
  upperTitle: {
    alignSelf: 'flex-start',
    color: Colors.textGray,
    fontFamily: Fonts.fontFamily,
    fontSize: `calc(${Fonts.fontSize} * 0.75)`,
    fontWeight: 400,
    marginTop: '0.25rem',
    textTransform: 'uppercase',
  },
  title: {
    height: '2.9rem',
  },
  titleContainer: {
    alignItems: 'flex-start',
    display: 'grid',
    marginTop: '-1.5rem',

    '& > a': {
      height: '2rem',
    },
  },
  subtitleContainer: {},
});

type Props = {
  type: 'forecast' | 'summary';
};

type Params = {
  id: string;
  taskType: string;
};

type KeyGetCCMTasksParams = keyof GetProgramTaskParam;

export const PODsMetricsForecastDetail = ({ type }: Props) => {
  const { id: podId, taskType: taskDefinitionId } = useParams<Params>();
  const history = useHistory();
  const query = useQueryParams();
  const styles = useStyles();

  const monthValue = moment(query.get('month'), DATE_FORMAT);
  const podNameValue = query.get('podName');
  const taskNameValue = query.get('taskName');

  const [taskDefinitions, setTaskDefinitions] = useState<PodRule[]>([]);
  const taskDefinition = useMemo(
    () => taskDefinitions?.find(({ id }) => String(id) === taskDefinitionId),
    [taskDefinitionId, taskDefinitions],
  );

  const [appointmentTypes, setAppointmentTypes] = useState<string[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [podConfigs, setPodConfigs] = useState<PodConfig[]>([]);
  const [touchpointReasons, setTouchpointReasons] = useState<string[]>([]);

  const podConfig = useMemo(
    () => podConfigs?.find(({ id }) => String(id) === podId),
    [podId, podConfigs],
  );

  const [anchorActions, setAnchorActions] = useState<Element>();

  const [filters, setFilters] = useState<
    {
      [x in KeyGetCCMTasksParams]?: FilterItem;
    }
  >();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const [month, setMonth] = useState<Moment>(
    (monthValue.isValid() ? monthValue : moment().add(1, 'month')).endOf(
      'month',
    ),
  );

  const [tasks, setTasks] = useState<ProgramTask[]>([]);
  const [pagination, setPagination] = useState<PaginationType>();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingFilters, setLoadingFilters] = useState<boolean>(false);
  const [sort, setSort] = useState<SortOption>(SORT_OPTIONS[0]);

  const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);
  const [openReassignModal, setOpenReassignModal] = useState<boolean>(false);
  const [openOverrideModal, setOpenOverrideModal] = useState<boolean>(false);
  const [openReviewModal, setOpenReviewModal] = useState<boolean>(false);
  const [reassignEmloyee, setReassignEmployee] = useState<Employee>();
  const [overrideEmloyee, setOverrideEmployee] = useState<Employee>();
  const [overrideType, setOverrideType] = useState<PodRule>();

  const selectedTasks = selectAll
    ? (pagination?.total || 0) - selectedIds.length
    : selectedIds.length;

  const getInitialData = async () => {
    setLoadingFilters(true);
    const [
      touchpointReasons,
      employees,
      languages,
      appointmentTypes,
      items,
      pods,
    ] = await Promise.all([
      CacheServices.getCompleteTouchpointReasons(),
      CacheServices.getEmployees(),
      CacheServices.getLanguages(),
      CacheServices.getNPAppointmentTypes(),
      CacheServices.getPodRules(),
      CacheServices.getPodConfigs(),
    ]);
    setAppointmentTypes(appointmentTypes);
    setEmployees(employees);
    setLanguages(languages);
    setTaskDefinitions(items);
    setTouchpointReasons(touchpointReasons);
    setPodConfigs(pods);
    setLoadingFilters(false);
  };

  const filtersData = useMemo(
    () => ({
      employees: Employee.toSelectable(
        employees.filter((e) =>
          podConfig?.isEmployeeInPod(e, taskDefinition?.roles),
        ),
      ),
      languages: Language.toSelectable(languages).filter(
        ({ disabled }) => !disabled,
      ),
    }),
    [employees, languages, podConfig, taskDefinition],
  );

  const getTasks = async () => {
    if (!filters) return;

    const offset = page * pageSize;
    if (offset !== 0 && offset < tasks?.length) return;

    setLoading(true);
    try {
      const currentFilters = getServerFilters(filters);
      const { items, pagination } = await fetchPogramTasks({
        ...currentFilters,
        month,
        taskDefinitionId: [taskDefinitionId].filter(Boolean),
        podId: [podId],
        offset,
        limit: pageSize,
        sort: sort.sort,
      });

      if (page > 0) {
        setTasks([...tasks, ...items]);
      } else {
        setTasks(items);
      }
      setPagination(pagination);
    } catch (e) {
      showGlobalError(e as string);
    }
    setLoading(false);
  };

  const getPageTitle = () => {
    return (
      <div className={styles.titleContainer}>
        <div
          className={classNames('flex gap spaced', styles.subtitleContainer)}
        >
          <Button
            href="blank"
            className={styles.back}
            data-cy="sms-back-button"
            onClick={() => history.goBack()}
          >
            <Icon className="fa fa-arrow-left" color="green" size="sm" />
            &nbsp;{translate('global.back')}
          </Button>
          <span className={styles.upperTitle}>{taskNameValue}</span>
        </div>
        <div className={classNames('flex', styles.title)}>
          <span>
            {translate(
              `pods.metrics.forecastDetail.${
                type === 'summary' ? 'titleSummary' : 'title'
              }`,
              {
                pod: podNameValue || 'POD',
              },
            )}
          </span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!filters) return;

    const serverFilters = getServerFilters(filters);

    history.replace(
      `?podName=${podNameValue || ''}` +
        `&taskName=${taskNameValue || ''}` +
        `&month=${month.format(DATE_FORMAT)}` +
        `${
          serverFilters?.assigneeId?.length
            ? `&${serverFilters?.assigneeId
                ?.map((id: number) => `assigneeId=${id}`)
                .join('&')}`
            : ''
        }` +
        `${
          serverFilters?.statusId?.length
            ? `&${serverFilters?.statusId
                ?.map((id: string) => `statusId=${id}`)
                .join('&')}`
            : ''
        }`,
    );
  }, [month, filters?.assigneeId, filters?.statusId]);

  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    getTasks();
  }, [page, pageSize, month, filters, sort]);

  useEffect(() => {
    if (employees?.length) {
      const storedFilters = loadNewFilters(
        '',
        {
          assigneeId: {
            data: Employee.toSelectable(employees),
            label: translate('pods.metrics.forecastDetail.filter.assignee'),
            multiple: true,
          },
          statusId: {
            data: Enum.toSelectable(ProgramTaskStatus.asArray),
            label: translate('incidents.filters.brand'),
            multiple: true,
          },
        } as { [x in 'assigneeId' | 'statusId']: any },
        query,
      );
      setFilters(storedFilters);
    }
  }, [employees]);

  const showChecks =
    (type === 'forecast' || month.isSame(moment(), 'month')) &&
    taskDefinition?.id !== PodRule.ENGAGEMENT_BIANNUAL;

  return (
    <Panel>
      <Panel.Heading title={getPageTitle()} filtersV2>
        <Panel.Actions>
          <Select
            className={styles.date}
            data-cy="pod-reporting-filters-month"
            disableClearable
            getItemLabel={(date: Moment) =>
              date.format('MMM YYYY').toLocaleUpperCase()
            }
            items={type === 'forecast' ? FUTURE_MONTHS : PAST_MONTHS}
            onChange={(month?: Moment) => month && setMonth(month)}
            placeholder={translate('pods.metrics.forecastDetail.month')}
            value={month}
          />
          {showChecks && (
            <IconButton
              data-cy="pod-forecast-selection-actions"
              disabled={!selectedTasks}
              onClick={(evt) => {
                evt.stopPropagation();
                if (anchorActions) {
                  setAnchorActions(undefined);
                } else {
                  setAnchorActions(
                    evt.currentTarget?.parentElement?.parentElement ||
                      undefined,
                  );
                }
              }}
              tooltip={translate('pods.metrics.forecastDetail.actions')}
            >
              <MoreVertIcon />
            </IconButton>
          )}
          <Menu
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            anchorEl={anchorActions}
            open={Boolean(anchorActions)}
            onClose={() => setAnchorActions(undefined)}
            PaperProps={{
              style: {
                marginTop: '0.5rem',
                minWidth: '17rem',
              },
            }}
          >
            <MenuItem
              key="pod-metrics-reassign"
              onClick={() => {
                setOverrideEmployee(undefined);
                setOverrideType(undefined);
                setReassignEmployee(undefined);
                setOpenReassignModal(true);
                setAnchorActions(undefined);
              }}
            >
              <span className={styles.actionItem}>
                {translate('pods.metrics.forecastDetail.actionReassign')}
              </span>
            </MenuItem>
            <MenuItem
              key="pod-metrics-reassign"
              disableTouchRipple={taskDefinition && !taskDefinition.flexible}
              disableRipple={taskDefinition && !taskDefinition.flexible}
              onClick={() => {
                setOverrideEmployee(undefined);
                setOverrideType(undefined);
                setReassignEmployee(undefined);
                setOpenOverrideModal(true);
                setAnchorActions(undefined);
              }}
            >
              {taskDefinition && !taskDefinition.flexible ? (
                <Tooltip
                  text={translate(
                    'pods.metrics.forecastDetail.actionOverrideTooltip',
                  )}
                  enterDelay={200}
                  followCursor
                >
                  <span
                    className={classNames(
                      styles.actionItem,
                      styles.disabledOption,
                    )}
                    onClick={(evt) => evt.stopPropagation()}
                  >
                    {translate('pods.metrics.forecastDetail.actionOverride')}
                  </span>
                </Tooltip>
              ) : (
                <span className={styles.actionItem}>
                  {translate('pods.metrics.forecastDetail.actionOverride')}
                </span>
              )}
            </MenuItem>
          </Menu>
        </Panel.Actions>
        {showChecks && (
          <Panel.Tabs className="flex spaced buttons">
            <span>
              {translate('pods.metrics.forecastDetail.selectedItems', {
                count: selectedTasks,
              })}
            </span>
            <div className="flex gap">
              <IconButton
                data-cy="pod-forecast-selection-button"
                disabled={selectAll}
                onClick={() => {
                  setSelectedIds([]);
                  setSelectAll(!selectAll);
                }}
                tooltip={translate('pods.metrics.forecastDetail.selection')}
              >
                <CustomSelectionIcon />
              </IconButton>
              <IconButton
                data-cy="pod-forecast-clear-button"
                disabled={!selectedIds?.length && !selectAll}
                onClick={() => {
                  setSelectedIds([]);
                  setSelectAll(false);
                }}
                tooltip={translate('global.clear')}
              >
                <ClearIcon />
              </IconButton>
            </div>
          </Panel.Tabs>
        )}
        <Panel.FilterBar
          chips={filters}
          inputs={
            <ForecastDetailTaskFilterBar
              data={filtersData}
              filters={filters}
              loading={loadingFilters}
              onChange={(filters) => {
                setPage(0);
                setFilters(filters);
              }}
            />
          }
          onClearFilters={() => {
            setFilters({});
          }}
          onDeleteFilter={(key: string) => {
            setFilters({
              ...filters,
              [key]: undefined,
            });
          }}
          onChangeSort={(sort) => {
            setPage(0);
            setSort(sort);
          }}
          onOpenFilters={() => setOpenFilterModal(!openFilterModal)}
          sortOptions={SORT_OPTIONS}
          sortDefault={sort}
        />
      </Panel.Heading>
      <Panel.Body loading={loading}>
        <ForecastDetailTaskFilterModal
          data={{
            appointmentTypes: [
              UNASSIGNED_TYPE,
              ...Enum.toSelectableFromString(appointmentTypes),
            ],
            languages: Language.toSelectable(languages).filter(
              ({ disabled }) => !disabled,
            ),
            employees: Employee.toSelectable(employees),
            taskDefinitions: PodRule.toSelectable(taskDefinitions),
            touchpointReasons: [
              UNASSIGNED_TYPE,
              ...Enum.toSelectableFromString(touchpointReasons),
            ],
          }}
          filters={filters}
          open={openFilterModal}
          onChange={(pendingFilters) => {
            setPage(0);
            setFilters(pendingFilters);
          }}
          onClose={() => setOpenFilterModal(false)}
        />
        <ForecastDetailTaskTable
          allChecked={
            !!(
              tasks?.length &&
              [...tasks]
                .slice(page * pageSize, (page + 1) * pageSize)
                .every((task) => selectAll !== selectedIds.includes(task.id))
            )
          }
          blacklist={selectAll}
          defaultPageSize={DEFAULT_PAGE_SIZE}
          items={tasks}
          onChangePage={setPage}
          onChangePageSize={(ps) => {
            setPage(0);
            setPageSize(ps);
          }}
          onSelectAll={(checked) => {
            const ids =
              [...tasks]
                .slice(page * pageSize, (page + 1) * pageSize)
                .map((task) => task.id) || [];
            if (!ids.length) return;

            setSelectedIds(
              selectAll === checked
                ? uniq([...selectedIds, ...ids])
                : selectedIds.filter((id) => !ids.includes(id)),
            );
          }}
          onSelectTask={(task) => {
            const idx = selectedIds?.findIndex((id) => id === task.id);
            if (idx === -1) {
              selectedIds.push(task.id);
            } else {
              selectedIds.splice(idx, 1);
            }
            setSelectedIds([...selectedIds]);
          }}
          pagination={pagination}
          selectedIds={selectedIds}
          showChecks={showChecks}
        />
      </Panel.Body>
      {podConfig && taskDefinition && (
        <PodTaskReassignModal
          employees={employees}
          open={openReassignModal}
          podConfig={podConfig}
          taskType={taskDefinition}
          review
          onSubmit={(employee: Employee) => {
            setReassignEmployee(employee);
            setOpenReviewModal(true);
          }}
          onClose={() => setOpenReassignModal(false)}
        />
      )}
      {podConfig && taskDefinition?.flexible && (
        <PodTaskOverrideModal
          employees={employees}
          open={openOverrideModal}
          podConfig={podConfig}
          taskTypes={taskDefinitions?.filter(
            ({ id, flexible }) => id !== taskDefinition?.id && flexible,
          )}
          review
          onSubmit={(taskType: PodRule, employee: Employee) => {
            setOverrideEmployee(employee);
            setOverrideType(taskType);
            setOpenReviewModal(true);
          }}
          onClose={() => setOpenOverrideModal(false)}
        />
      )}
      {podConfig && taskDefinition && (
        <PodTaskReviewModal
          assignee={overrideType ? overrideEmloyee : reassignEmloyee}
          filters={{
            assigneeId: (filters?.assigneeId
              ?.value as FilterSelectablev2[])?.map(({ value }) => value),
            language: (filters?.language?.value as FilterSelectablev2[])?.map(
              ({ value }) => value,
            ),
            month,
            podConfig,
            state: (filters?.state?.value as FilterSelectablev2[])?.map(
              ({ value }) => value as string,
            ),
            statusId: (filters?.statusId?.value as FilterSelectablev2[])?.map(
              ({ value }) => value as number,
            ),

            taskDefinition,
          }}
          open={openReviewModal}
          selectAll={selectAll}
          selectedIds={selectedIds}
          taskType={overrideType}
          onSubmit={async () => {
            const commonFilters: PodTaskBulkCommonParams = {
              filter: selectAll
                ? {
                    assigneeIds: (filters?.assigneeId
                      ?.value as FilterSelectablev2[])?.map(
                      ({ value }) => value,
                    ),
                    excludedTaskIds: selectedIds,
                    languages: (filters?.language
                      ?.value as FilterSelectablev2[])?.map(
                      ({ value }) => value,
                    ),
                    month,
                    podIds: podConfig ? [podConfig.id] : undefined,
                    states: (filters?.state
                      ?.value as FilterSelectablev2[])?.map(
                      ({ value }) => value,
                    ),
                    statusIds: (filters?.statusId
                      ?.value as FilterSelectablev2[])?.map(
                      ({ value }) => value,
                    ),
                    taskDefinitionIds: [taskDefinition.id],
                  }
                : undefined,
              taskIds: selectAll ? undefined : selectedIds,
            };

            if (overrideType && overrideEmloyee) {
              await bulkOverridePodTasks({
                ...commonFilters,
                assigneeId: overrideEmloyee.id,
                taskDefinitionId: overrideType.id,
              });
            } else if (reassignEmloyee) {
              await bulkReassignPodTasks({
                ...commonFilters,
                assigneeId: reassignEmloyee.id,
              });
            }
            setOpenReassignModal(false);
            setOpenOverrideModal(false);
            setOpenReviewModal(false);
            setSelectedIds([]);
            setSelectAll(false);
            if (page !== 0) {
              setPage(0);
            } else {
              getTasks();
            }
          }}
          onClose={() => setOpenReviewModal(false)}
        />
      )}
    </Panel>
  );
};

export default PODsMetricsForecastDetail;
